import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MuxPlayer from "@mux/mux-player-react";
import { ChevronRight, Lock, NotebookPen, Save } from "lucide-react";
import type {
  SignedPlayback,
  ViewerNoteDTO,
  ViewerPlaylist,
} from "@demore/shared/types";
import { ViewerShell } from "./ViewerShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/input";
import { api } from "@/lib/api";
import { cn, formatDuration } from "@/lib/utils";
import { YoutubeEmbed } from "./YoutubeEmbed";

export function WatchPage() {
  const { token, videoRef: videoRefParam } = useParams<{ token: string; videoRef?: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["viewer-playlist", token],
    queryFn: () => api.get<ViewerPlaylist>(`/api/view/${token}/rate`),
    enabled: !!token,
  });
  const items = data?.items ?? [];
  const initialIdx = useMemo(() => {
    if (videoRefParam) {
      const i = items.findIndex((it) => it.videoRef === videoRefParam);
      // Bug 7: only pin the URL's video if it isn't already finished. Landing
      // on a completed video's URL (e.g. after a refresh) should fall through
      // to resumeIndex — the first INCOMPLETE video — not replay the finished one.
      if (i >= 0 && !items[i]?.completed) return i;
    }
    return data?.resumeIndex ?? 0;
  }, [items, videoRefParam, data?.resumeIndex]);
  const [activeIdx, setActiveIdx] = useState<number>(initialIdx);

  useEffect(() => {
    setActiveIdx(initialIdx);
  }, [initialIdx]);

  const active = items[activeIdx];

  const { data: playback } = useQuery({
    queryKey: ["playback", token, active?.videoRef],
    queryFn: () => api.post<SignedPlayback>(`/api/view/${token}/play/${active!.videoRef}`),
    enabled: !!active?.playable,
  });

  // Audit V1: notes -- single GET fetches all viewer notes for this issuance;
  // PUT upserts per video.
  const { data: notes } = useQuery({
    queryKey: ["viewer-notes", token],
    queryFn: () => api.get<ViewerNoteDTO[]>(`/api/view/${token}/notes`),
    enabled: !!token,
  });
  const noteByVideo = useMemo(() => {
    const m = new Map<string, string>();
    (notes ?? []).forEach((n) => m.set(n.videoRef, n.body));
    return m;
  }, [notes]);
  const [noteDraft, setNoteDraft] = useState<string>("");
  const [noteOpen, setNoteOpen] = useState(false);
  useEffect(() => {
    if (active) setNoteDraft(noteByVideo.get(active.videoRef) ?? "");
  }, [active?.videoRef, noteByVideo]);

  const saveNote = useMutation({
    mutationFn: () =>
      api.put<ViewerNoteDTO>(`/api/view/${token}/notes/${active!.videoRef}`, {
        body: noteDraft,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["viewer-notes", token] }),
  });

  const heartbeat = useMutation({
    mutationFn: (body: { videoRef: string; watchDurationSeconds: number; completionPercentage: number }) =>
      api.post(`/api/view/${token}/progress/heartbeat`, body),
  });
  const milestone = useMutation({
    mutationFn: (body: { videoRef: string; milestone: 25 | 50 | 75 | 100; watchDurationSeconds: number }) =>
      // keepalive: the 100% often fires right as we navigate to the next
      // video / done screen — without it the browser aborts the in-flight
      // POST and the pack never registers as completed.
      api.post(`/api/view/${token}/progress/milestone`, body, { keepalive: true }),
    retry: 3,
    // The dedupe ref is marked optimistically before mutate (so rapid
    // timeupdates don't double-fire); if the POST ultimately fails, roll the
    // ref back so the next timeupdate re-sends. Losing a 100 permanently was
    // the "Completed stays 0" defect (QA, live, 2026-07-04).
    onError: (_err, vars) => {
      const prev = vars.milestone === 25 ? 0 : vars.milestone - 25;
      if ((lastMilestone.current[vars.videoRef] ?? 0) <= vars.milestone) {
        lastMilestone.current[vars.videoRef] = prev;
      }
    },
  });
  // Bug 8: the client never called /rewatch, so rewatchCount stayed 0. A fresh
  // play-through (near the start) of an already-completed video is a rewatch;
  // the server validates completion and we emit at most one per mount per video.
  const rewatch = useMutation({
    mutationFn: (videoRef: string) =>
      api.post(`/api/view/${token}/progress/rewatch`, { videoRef }),
  });

  const lastMilestone = useRef<Record<string, number>>({});
  const completedRef = useRef<Record<string, boolean>>({});
  const rewatchedRef = useRef<Record<string, boolean>>({});
  // Heartbeat throttle. Was stored on the useMutation result object, which
  // TanStack recreates every render — so the throttle never held and one
  // playing tab fired ~4 heartbeats/second, tripping the per-IP API rate
  // limit and 429ing its own milestone POSTs (QA "Completed stays 0", live).
  const lastHeartbeatAt = useRef(0);

  function maybeRewatch(videoRef: string, t: number, pct: number) {
    const done = active?.completed || completedRef.current[videoRef];
    if (done && t < 5 && pct < 15 && !rewatchedRef.current[videoRef]) {
      rewatchedRef.current[videoRef] = true;
      rewatch.mutate(videoRef);
    }
  }

  // Shared progress reporter for both players (Mux timeupdate + YouTube poll).
  // The 100 milestone fires from 97%: scrubbing near the end, YouTube's
  // approximate getDuration(), and outro-skips all left packs at 99% forever
  // under a strict ==100 threshold.
  function reportProgress(videoRef: string, t: number, d: number) {
    if (!d) return;
    const pct = Math.round((t / d) * 100);
    maybeRewatch(videoRef, t, pct);
    if (Date.now() - lastHeartbeatAt.current > 5000) {
      lastHeartbeatAt.current = Date.now();
      heartbeat.mutate({
        videoRef,
        watchDurationSeconds: Math.floor(t),
        completionPercentage: pct,
      });
    }
    for (const m of [25, 50, 75, 100] as const) {
      const threshold = m === 100 ? 97 : m;
      if (pct >= threshold && (lastMilestone.current[videoRef] ?? 0) < m) {
        lastMilestone.current[videoRef] = m;
        milestone.mutate({
          videoRef,
          milestone: m,
          watchDurationSeconds: Math.floor(t),
        });
        if (m === 100) {
          completedRef.current[videoRef] = true;
          goNext();
        }
      }
    }
  }

  // Player 'ended' — the authoritative completion signal; posts the 100
  // even when no timeupdate/poll ever showed >=97% (short videos, seeks).
  function onPlayerEnded(videoRef: string, durationSeconds: number) {
    if ((lastMilestone.current[videoRef] ?? 0) >= 100) return;
    lastMilestone.current[videoRef] = 100;
    milestone.mutate({
      videoRef,
      milestone: 100,
      watchDurationSeconds: Math.floor(durationSeconds),
    });
    completedRef.current[videoRef] = true;
    goNext();
  }

  function onTimeUpdate(e: CustomEvent) {
    if (!active) return;
    const media = e.target as HTMLMediaElement | null;
    reportProgress(active.videoRef, media?.currentTime ?? 0, media?.duration ?? 0);
  }

  function goNext() {
    const nextPlayable = items.findIndex((it, i) => i > activeIdx && it.playable);
    if (nextPlayable >= 0) {
      const next = items[nextPlayable];
      if (!next) return;
      setActiveIdx(nextPlayable);
      nav(`/view/${token}/watch/${next.videoRef}`, { replace: true });
    } else {
      nav(`/view/${token}/done`);
    }
  }

  if (!data || !active) return <ViewerShell><div className="py-20 text-center">Loading…</div></ViewerShell>;

  return (
    <ViewerShell footerNote="demore">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="aspect-video bg-navy rounded-md overflow-hidden relative">
            {/* Audit V1: dispatch on playback source. YouTube uses an IFrame
                embed; Mux uses the Mux player as before. */}
            {!active.playable ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/70 gap-2">
                <Lock className="h-6 w-6" />
                <span className="text-sm">This content is above your access level.</span>
              </div>
            ) : playback?.source === "youtube" && playback.youtubeVideoId ? (
              <YoutubeEmbed
                key={active.videoRef}
                videoId={playback.youtubeVideoId}
                title={active.title}
                onProgress={(currentTime, duration) => {
                  if (!active) return;
                  reportProgress(active.videoRef, currentTime, duration);
                }}
                onEnded={(duration) => {
                  if (!active) return;
                  onPlayerEnded(active.videoRef, duration);
                }}
              />
            ) : playback?.playbackId ? (
              <MuxPlayer
                playbackId={playback.playbackId}
                tokens={playback.tokens}
                autoPlay
                streamType="on-demand"
                accentColor="#E6AC2A"
                onTimeUpdate={onTimeUpdate}
                onEnded={(e: CustomEvent) => {
                  if (!active) return;
                  const media = e.target as HTMLMediaElement | null;
                  onPlayerEnded(active.videoRef, media?.duration ?? 0);
                }}
                poster={active.thumbnailUrl ?? undefined}
                className="w-full h-full"
                // Audit V1: Mux Data attribution. The env_key is server-issued
                // (safe to expose) and attaches the rest of the view metadata
                // so Mux Data's dashboard breaks down by video + viewer.
                metadata={
                  playback.muxEnvKey
                    ? {
                        env_key: playback.muxEnvKey,
                        video_id: active.videoRef,
                        video_title: active.title,
                        player_name: "demore-viewer-watch",
                      }
                    : undefined
                }
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
                Loading playback…
              </div>
            )}
          </div>
          <div>
            <h2 className="font-head text-xl font-semibold">{active.title}</h2>
            <p className="text-sm text-navy-200 mt-1">{active.description}</p>
            <div className="flex gap-2 mt-3">
              <Button onClick={goNext}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              {/* Audit V1: notes panel toggle. Open by default on first
                  unopened note for this video. */}
              <Button
                variant="outline"
                onClick={() => setNoteOpen((v) => !v)}
              >
                <NotebookPen className="h-4 w-4 mr-1" />
                {noteOpen ? "Hide notes" : "Take notes"}
              </Button>
            </div>
          </div>

          {noteOpen && (
            <div className="bg-viewer-soft rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">
                  Your notes on this video
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => saveNote.mutate()}
                  disabled={saveNote.isPending}
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saveNote.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
              <Textarea
                rows={6}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Jot questions, reactions, follow-ups…"
              />
              <p className="text-[11px] text-navy-200">
                Only you and {data.senderFirstName} see these. They show on
                your re-visit and help the sales follow-up be relevant.
              </p>
            </div>
          )}
        </div>
        <aside>
          <div className="font-head text-sm font-semibold mb-2 text-gold">Up next</div>
          <ol className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {items.map((it, idx) => (
              <li
                key={it.videoRef}
                className={cn(
                  "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                  idx === activeIdx ? "bg-gold/15" : "hover:bg-viewer-soft",
                  !it.playable && "opacity-60",
                )}
                onClick={() => {
                  if (it.playable) {
                    setActiveIdx(idx);
                    nav(`/view/${token}/watch/${it.videoRef}`, { replace: true });
                  }
                }}
              >
                <div className="w-16 aspect-video bg-navy rounded shrink-0 overflow-hidden">
                  {it.thumbnailUrl && <img src={it.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-medium line-clamp-2">{it.title}</div>
                  <div className="text-navy-200 mt-0.5">
                    {formatDuration(it.durationSeconds)}
                    {it.completed && " · ✓"}
                    {!it.playable && " · Locked"}
                    {it.source === "youtube" && " · YouTube"}
                    {noteByVideo.get(it.videoRef) && " · 📝"}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </ViewerShell>
  );
}
